/**
 * mapClientViewUnitToReviewUnit
 *
 * Maps a version-snapshot unit + its enriched `ClientOfferViewUnit` /
 * `PublicOfferViewUnit` counterpart onto `ClientOfferReviewUnit` — the view
 * model the redesigned client-offer-review-v2 component set consumes. See
 * design.md "Decision 2" for the full per-field source trace.
 *
 * `halves` is intentionally left `[]` here: it depends on the shared period
 * axis across every unit in the offer, which only the caller (composing
 * `build-review-periods.ts`) can compute — see that module's per-unit
 * halves builder.
 */
import type { ClientOfferViewUnit, OfferSnapshotUnit, PublicOfferViewUnit } from '@ooh/contracts';
import { MediaUnitDetailsSchema } from '../../lib/media-unit-details';
import { getSnapshotClientPrice } from '../../lib/snapshot-price-helpers';
import { isUnitFullySold } from './map-snapshot-unit-to-client-item';
import type { ClientOfferReviewUnit } from '../../client-offer-review';

/** Matches the "{width} x {height} m" fallback written by buildMediaUnitDetails
 * (apps/api offer-versions.service.ts) — numeric(8,2) columns always render two
 * decimal places, e.g. "4.00 x 3.00 m". */
const DIMENSIONS_PATTERN = /^(\d+(?:\.\d+)?) x (\d+(?:\.\d+)?) m$/;

/**
 * Parses the formatted `dimensions` fallback string into numeric metres,
 * for callers that need the historical/frozen record (see
 * `useFrozenDimensions` below) rather than a unit's current live size.
 * Returns `null` on a missing/malformed string (older snapshot, or a future
 * change to the server's formatting) so the caller falls back to the live
 * enriched unit's size instead of silently rendering "0.00 x 0.00 m".
 */
function parseFrozenDimensionsM(
  dimensions: string | undefined,
): { widthM: number; heightM: number } | null {
  const match = dimensions?.match(DIMENSIONS_PATTERN);
  if (match?.[1] == null || match[2] == null) return null;
  return { widthM: Number(match[1]), heightM: Number(match[2]) };
}

/** Lowercase, diacritic-stripped, dash-joined slug — map-legend grouping only, no stable format code exists yet. */
function slugifyFormat(format: string): string {
  return format
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

type MediaUnitDetails = ReturnType<typeof MediaUnitDetailsSchema.parse>;

/** Client-safe identity fields — supplier/supplierNumber gated on `showSupplierIdentity` (OQ-5/Decision 6). */
function buildIdentityFields(details: Partial<MediaUnitDetails>, showSupplierIdentity: boolean) {
  return {
    grw: details.grwId ?? '',
    supplierNumber: showSupplierIdentity ? (details.supplierNumber ?? null) : null,
    supplier: showSupplierIdentity ? (details.supplier ?? null) : null,
    address: details.address ?? '',
    city: details.city ?? '',
    voivodeship: details.voivodeship ?? null,
  };
}

/** Nullable coordinates never coerced to 0 (CU-86cay892c). */
function buildCoordinateFields(enriched: ClientOfferViewUnit | PublicOfferViewUnit | null) {
  return {
    lat: enriched?.latitude ?? null,
    lng: enriched?.longitude ?? null,
    locationDescription: enriched?.locationDescription ?? null,
  };
}

/**
 * thumbnailUrl/displayUrl are read independently off `enriched`, unchanged —
 * this is the single mapping choke point that resolves the rendition choice
 * for every downstream client-offer-review render site
 * (media-photo-derivatives: "the rendition choice SHALL be made once where
 * a photo-bearing payload is mapped into a view model, not repeated in each
 * presentational component"). The API already guarantees neither field is
 * null when the photo exists, so no `?? original` fallback belongs here.
 */
function buildPhotoFields(enriched: ClientOfferViewUnit | PublicOfferViewUnit | null) {
  return {
    thumbnailUrl: enriched?.thumbnailUrl ?? null,
    displayUrl: enriched?.displayUrl ?? null,
  };
}

/**
 * widthM/heightM + photo/coordinate fields. widthM/heightM come from the
 * frozen snapshot text (`frozenDimensionsM`) when the caller passed
 * `useFrozenDimensions` — physical dimensions are historical record for a
 * version snapshot, unlike photo/coordinates below which are always
 * resolved live (same divergence the now-removed v1
 * `mapSnapshotUnitToClientItemWithFrozenDimensions` documented).
 */
function buildDisplayFields(
  enriched: ClientOfferViewUnit | PublicOfferViewUnit | null,
  frozenDimensionsM: { widthM: number; heightM: number } | null,
) {
  return {
    widthM: frozenDimensionsM ? frozenDimensionsM.widthM : (enriched?.widthM ?? 0),
    heightM: frozenDimensionsM ? frozenDimensionsM.heightM : (enriched?.heightM ?? 0),
    ...buildPhotoFields(enriched),
    ...buildCoordinateFields(enriched),
  };
}

export function mapClientViewUnitToReviewUnit(
  snapshotUnit: OfferSnapshotUnit,
  enriched: ClientOfferViewUnit | PublicOfferViewUnit | null,
  showSupplierIdentity = false,
  /**
   * True only for the internal read-only version-preview screen
   * (OfferVersionSnapshotView) — a historical version snapshot must show the
   * width/height that were true when it was published, not a unit's current
   * live size, which may have changed since.
   */
  useFrozenDimensions = false,
): ClientOfferReviewUnit {
  const details = MediaUnitDetailsSchema.safeParse(snapshotUnit.mediaUnitDetails).data ?? {};
  const format = details.format ?? '';
  const frozenDimensionsM = useFrozenDimensions ? parseFrozenDimensionsM(details.dimensions) : null;

  return {
    id: String(snapshotUnit.id),
    mediaUnitId: snapshotUnit.mediaUnitId,
    ...buildIdentityFields(details, showSupplierIdentity),
    format,
    formatKey: slugifyFormat(format),
    dimensions: details.dimensions ?? '',
    ...buildDisplayFields(enriched, frozenDimensionsM),
    price: getSnapshotClientPrice(snapshotUnit) ?? null,
    sold: isUnitFullySold(enriched),
    halves: [],
  };
}
